import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { fmtDate } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { FileText, Image as ImageIcon, UploadCloud, Loader2, Download } from "lucide-react";

export default function DrawingsSection({ jobId, drawings = [], canUpload }) {
  const qc = useQueryClient();
  const [uploading, setUploading] = useState(false);

  const openFile = async (d) => {
    try {
      const res = await api.get(`/files/${d.storage_path}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error("Could not open file");
    }
  };

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/jobs/${jobId}/drawings`, fd);
      toast.success("Drawing uploaded");
      qc.invalidateQueries({ queryKey: ["job", jobId] });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  const isImg = (ext) => ["jpg", "jpeg", "png", "webp"].includes(ext);

  return (
    <div data-testid="drawings-section">
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-heading text-xl font-bold">Job Drawings</h2>
        {canUpload && (
          <label className="inline-flex">
            <input type="file" className="hidden" accept=".drw,.dwg,.dxf,.pdf,.jpg,.jpeg,.png,.webp" onChange={onPick} data-testid="drawing-file-input" />
            <span className="inline-flex items-center px-3 py-2 rounded-md bg-primary text-primary-foreground text-sm font-medium cursor-pointer hover:bg-primary/90" data-testid="upload-drawing-button">
              {uploading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <UploadCloud className="h-4 w-4 mr-2" />} Upload
            </span>
          </label>
        )}
      </div>
      {drawings.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
          No drawings uploaded yet.
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {drawings.map((d) => (
            <button key={d.id} onClick={() => openFile(d)} data-testid={`drawing-${d.id}`}
              className="group rounded-lg border border-border bg-card p-3 text-left hover:border-primary/50 transition-colors">
              <div className="flex items-center justify-center h-16 rounded-md bg-accent mb-2">
                {isImg(d.ext) ? <ImageIcon className="h-7 w-7 text-secondary" /> : <FileText className="h-7 w-7 text-primary" />}
              </div>
              <p className="text-xs font-medium truncate">{d.original_filename}</p>
              <p className="text-[10px] text-muted-foreground truncate">{d.uploaded_by_name} · {fmtDate(d.uploaded_at)}</p>
              <span className="mt-1 inline-flex items-center gap-1 text-[10px] text-primary opacity-0 group-hover:opacity-100">
                <Download className="h-3 w-3" /> open
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
